import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ColumnDef } from "@tanstack/react-table";
import {
  Dumbbell,
  MoreHorizontal,
  Route,
  Ruler,
  Timer,
  Trash2,
  User,
} from "lucide-react";
import { DataTableColumnHeader } from "@/components/dataTable/DataTableColumnHeader";
import { Tables } from "@/lib/supabase/supabaseTypes";
import { EquipmentIcon } from "@/components/exercises/EquipmentIcon";
import {
  ExperienceLevelBadge,
  ExperienceLevelIcon,
} from "@/components/exercises/ExperienceLevelBadge";
import { fuzzyFilter, fuzzySort } from "../dataTable/DataTableUtils";
import { SYSTEM_ORG_UUID } from "@/lib/supabase/supabaseClient";
import { toSentenceCase } from "@/lib/utils/typeUtils";
import { canDeleteExercise, canEditExerciseDetails } from "./exerciseAccess";

export type Exercise = Tables<"exercises"> & { url?: string | null };

type ExerciseColumnsOptions = {
  userId?: string | null;
  canManageLibrary: boolean;
};

export const exerciseDisplayColumns: ColumnDef<Exercise>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ getValue }) => {
      return (
        <div className="w-full truncate font-medium p-2 text-secondary-foreground">
          {getValue() as string}
        </div>
      );
    },
    filterFn: fuzzyFilter,
    sortingFn: fuzzySort,
    meta: {
      headerClassName: "w-[280px]",
      cellClassName: "w-[280px] max-w-0",
    },
  },
  {
    id: "primary_muscles",
    accessorFn: (row) => row.primary_muscles,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Primary Muscles" />
    ),
    cell({ getValue }) {
      const bodyParts = getValue() as string[];
      const sent = bodyParts.map((bp) => toSentenceCase(bp));
      return <div className="truncate">{sent.join(", ")}</div>;
    },
    enableColumnFilter: true,
    filterFn: (row, id, value) => {
      const bodyParts = row.getValue(id) as string[];
      return bodyParts.some((bp) => value.includes(bp));
    },
    meta: {
      headerClassName: "w-[240px]",
      cellClassName: "w-[240px] max-w-0",
    },
  },
  {
    id: "equipment",
    filterFn: "arrIncludesSome",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Equipment" />
    ),
    accessorKey: "equipment",
    cell: ({ getValue }) => {
      const equipment = getValue() as string;
      if (!equipment) {
        return "None";
      }
      return toSentenceCase(equipment);
    },
    meta: {
      alwaysHide: true,
      headerClassName: "w-[160px]",
      cellClassName: "w-[160px]",
    },
  },
  {
    id: "type",
    filterFn: "arrIncludesSome",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    accessorKey: "type",
    cell: ({ getValue }) => {
      const type = getValue() as string;
      return toSentenceCase(type);
    },
    meta: {
      headerClassName: "w-[120px]",
      cellClassName: "w-[120px]",
    },
  },
  {
    id: "experience",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Experience" />
    ),
    accessorKey: "experience",
    filterFn: "arrIncludesSome",
    cell: ({ getValue }) => {
      const experience = getValue() as string;
      if (!experience) {
        return null;
      }
      return <ExperienceLevelBadge level={experience} />;
    },
    meta: {
      headerClassName: "w-[140px]",
      cellClassName: "w-[140px]",
    },
  },
  {
    id: "org_id",
    accessorKey: "org_id",
    filterFn: (row, id, value) => {
      const orgId = row.getValue(id) as string;
      const hideTrainrr = value as boolean;
      return hideTrainrr ? orgId !== SYSTEM_ORG_UUID : false;
    },
    meta: {
      alwaysHide: true,
    },
  },
];

export const columns = ({
  userId,
  canManageLibrary,
}: ExerciseColumnsOptions): ColumnDef<Exercise>[] => [
  ...exerciseDisplayColumns,
  {
    id: "Actions",
    cell: ({ row, onRowSelected, onRowDeleted }) => {
      const exercise = row.original as Exercise;
      const canEdit = canEditExerciseDetails(exercise, {
        userId,
        canManageLibrary,
      });
      const canDelete = canDeleteExercise(exercise, {
        userId,
        canManageLibrary,
      });
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onRowSelected?.(exercise);
              }}
            >
              {canEdit ? "Edit" : "View"}
            </DropdownMenuItem>
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onRowDeleted?.(exercise);
                  }}
                  variant="destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    meta: {
      headerClassName: "w-[72px]",
      cellClassName: "w-[72px]",
    },
  },
];

export const equipmentFilter = [
  {
    value: "dumbbell",
    label: "Dumbbell",
    leading: <EquipmentIcon equipment="dumbbell" />,
  },
  {
    value: "barbell",
    label: "Barbell",
    leading: <EquipmentIcon equipment="barbell" />,
  },
  {
    value: "kettlebells",
    label: "Kettlebells",
    leading: <EquipmentIcon equipment="kettlebells" />,
  },
  {
    value: "machine",
    label: "Machine",
    leading: <EquipmentIcon equipment="machine" />,
  },
  {
    value: "cable",
    label: "Cable",
    leading: <EquipmentIcon equipment="cable" />,
  },
  {
    value: "body only",
    label: "Body Only",
    leading: <EquipmentIcon equipment="body only" />,
  },
  {
    value: "bands",
    label: "Bands",
    leading: <EquipmentIcon equipment="bands" />,
  },
  {
    value: "e-z curl bar",
    label: "E-Z Curl Bar",
    leading: <EquipmentIcon equipment="e-z curl bar" />,
  },
  {
    value: "exercise ball",
    label: "Exercise Ball",
    leading: <EquipmentIcon equipment="exercise ball" />,
  },
  {
    value: "medicine ball",
    label: "Medicine Ball",
    leading: <EquipmentIcon equipment="medicine ball" />,
  },
  {
    value: "foam roll",
    label: "Foam Roll",
    leading: <EquipmentIcon equipment="foam roll" />,
  },
];

export const typeFilter = [
  { value: "strength", label: "Strength" },
  { value: "stretching", label: "Stretching" },
  { value: "cardio", label: "Cardio" },
  { value: "plyometrics", label: "Plyometrics" },
];

export const experienceFilter = [
  {
    value: "beginner",
    label: "Beginner",
    leading: <ExperienceLevelIcon level="beginner" />,
  },
  {
    value: "intermediate",
    label: "Intermediate",
    leading: <ExperienceLevelIcon level="intermediate" />,
  },
  {
    value: "expert",
    label: "Expert",
    leading: <ExperienceLevelIcon level="expert" />,
  },
];

export const trackingTypeFilter = [
  {
    value: "strength",
    label: "Strength",
    subLabel: "Weight x reps",
    Icon: Dumbbell,
  },
  {
    value: "bodyweight",
    label: "Bodyweight",
    subLabel: "Reps only",
    Icon: User,
  },
  {
    value: "timed",
    label: "Timed",
    subLabel: "Time",
    Icon: Timer,
  },
  {
    value: "distance_short",
    label: "Distance (short)",
    subLabel: "Distance + time",
    Icon: Ruler,
  },
  {
    value: "distance_long",
    label: "Distance (long)",
    subLabel: "Distance + time",
    Icon: Route,
  },
];

export const muscleGroups = [
  { value: "abdominals", label: "Abdominals" },
  { value: "chest", label: "Chest" },
  { value: "triceps", label: "Triceps" },
  { value: "biceps", label: "Biceps" },
  { value: "lower back", label: "Lower Back" },
  { value: "middle back", label: "Middle Back" },
  { value: "lats", label: "Lats" },
  { value: "traps", label: "Traps" },
  { value: "shoulders", label: "Shoulders" },
  { value: "forearms", label: "Forearms" },
  { value: "glutes", label: "Glutes" },
  { value: "hamstrings", label: "Hamstrings" },
  { value: "calves", label: "Calves" },
  { value: "quadriceps", label: "Quadriceps" },
];
